import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { ChartCard } from './ChartCard'
import { EmptyState } from './StatusIndicator'
import { formatBytes, formatBytesPerSec, deriveRateSeries } from './format'
import type { FilesystemRow, SeriesPoint } from '../../types/monitoring'

export function DiskPanel({ filesystems, ioSeries, loading, error }: {
  filesystems: FilesystemRow[]; ioSeries: SeriesPoint[]; loading: boolean; error?: string
}) {
  const readRate = deriveRateSeries(ioSeries, 'read_bytes')
  const writeRate = deriveRateSeries(ioSeries, 'write_bytes')
  const merged = readRate.map((r, i) => ({ time: r.time, read: r.value, write: writeRate[i]?.value ?? 0 }))

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
        <h2 className="font-bold text-secondary mb-4">Mounted Filesystems</h2>
        {loading ? (
          <div className="h-56 bg-neutral-100 rounded-lg animate-pulse" />
        ) : !filesystems.length ? (
          <EmptyState />
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr className="text-left">
                  <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">Mount</th>
                  <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide">FS</th>
                  <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide text-right">Used</th>
                  <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide text-right">Total</th>
                  <th className="py-2 px-2 text-neutral-400 font-medium text-xs uppercase tracking-wide w-28">Usage</th>
                </tr>
              </thead>
              <tbody>
                {filesystems.map((fs) => (
                  <tr key={fs.mountpoint} className="hover:bg-neutral-50 transition-colors">
                    <td className="py-2.5 px-2 border-t border-neutral-100 font-medium text-neutral-800 truncate max-w-[160px]">{fs.mountpoint}</td>
                    <td className="py-2.5 px-2 border-t border-neutral-100 text-neutral-500">{fs.fstype}</td>
                    <td className="py-2.5 px-2 border-t border-neutral-100 text-right text-neutral-600">{formatBytes(fs.used)}</td>
                    <td className="py-2.5 px-2 border-t border-neutral-100 text-right text-neutral-600">{formatBytes(fs.total)}</td>
                    <td className="py-2.5 px-2 border-t border-neutral-100">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${(fs.percent ?? 0) > 90 ? 'bg-red-500' : (fs.percent ?? 0) > 70 ? 'bg-amber-500' : 'bg-purple-500'}`}
                            style={{ width: `${Math.min(fs.percent ?? 0, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-neutral-600 tabular-nums w-9 text-right">
                          {(fs.percent ?? 0).toFixed(0)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ChartCard title="Disk I/O Throughput" subtitle="Read / write bytes per second" loading={loading} error={error} isEmpty={!merged.length}>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={merged} margin={{ left: -20, right: 8 }}>
            <defs>
              <linearGradient id="diskReadGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#059669" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="diskWriteGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#dc2626" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#dc2626" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
              tickFormatter={(t: string) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
            <YAxis tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false} tickFormatter={(v) => formatBytes(v)} />
            <Tooltip formatter={(v: number, name: string) => [formatBytesPerSec(v), name === 'read' ? 'Read' : 'Write']}
              labelFormatter={(l) => new Date(l).toLocaleString()} />
            <Area type="monotone" dataKey="read" stroke="#059669" strokeWidth={2} fill="url(#diskReadGradient)" isAnimationActive={false} />
            <Area type="monotone" dataKey="write" stroke="#dc2626" strokeWidth={2} fill="url(#diskWriteGradient)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  )
}
